import React from 'react';
import { AlertTriangle, Pill, Clock, ShoppingCart } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const ReviewSuggestions = ({ currentSuggestion, loading, handleApprovePrescription, fetchMedicineLinks, setActiveTab }) => {
  if (!currentSuggestion) return null;

  return (
    <div className="space-y-6">
      {/* Patient Info Summary */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <h3 className="text-xl font-bold text-blue-800 mb-3">Patient Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><p className="text-gray-600">Name</p><p className="font-semibold">{currentSuggestion.patient_info.patient_name}</p></div>
          <div><p className="text-gray-600">Age/Gender</p><p className="font-semibold">{currentSuggestion.patient_info.age} years, {currentSuggestion.patient_info.gender}</p></div>
          <div><p className="text-gray-600">Diagnosis</p><p className="font-semibold">{currentSuggestion.diagnosis.diagnosis}</p></div>
          <div><p className="text-gray-600">AI Confidence</p><p className="font-semibold">{(currentSuggestion.ai_confidence_score * 100).toFixed(0)}%</p></div>
        </div>
      </Card>

      {/* Warnings & Contraindications */}
      {(currentSuggestion.warnings.length > 0 || currentSuggestion.contraindications.length > 0 || currentSuggestion.drug_interactions.length > 0) && (
        <Card className="p-6 bg-red-50 border-red-200">
          <h3 className="text-xl font-bold text-red-800 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />Important Warnings
          </h3>
          {currentSuggestion.drug_interactions.length > 0 && (
            <div className="mb-4">
              <p className="font-semibold text-red-700 mb-2">Drug Interactions:</p>
              {currentSuggestion.drug_interactions.map((interaction, i) => (
                <div key={`interaction-${i}`} className="bg-white p-3 rounded mb-2 border border-red-200">
                  <p className="font-semibold text-red-600">{interaction.drug1} / {interaction.drug2} ({interaction.severity})</p>
                  <p className="text-sm text-gray-700">{interaction.description}</p>
                  <p className="text-sm text-blue-600 mt-1">{interaction.recommendation}</p>
                </div>
              ))}
            </div>
          )}
          {currentSuggestion.contraindications.length > 0 && (
            <div className="mb-4">
              <p className="font-semibold text-red-700 mb-2">Contraindications:</p>
              <ul className="list-disc ml-5 text-red-600">
                {currentSuggestion.contraindications.map((contra, i) => <li key={`contra-${i}`}>{contra}</li>)}
              </ul>
            </div>
          )}
          {currentSuggestion.warnings.length > 0 && (
            <div>
              <p className="font-semibold text-red-700 mb-2">Warnings:</p>
              <ul className="list-disc ml-5 text-gray-700">
                {currentSuggestion.warnings.map((warning, i) => <li key={`warn-${i}`}>{warning}</li>)}
              </ul>
            </div>
          )}
        </Card>
      )}

      {/* Suggested Medications */}
      <Card className="p-6">
        <h3 className="text-xl font-bold text-blue-800 mb-4 flex items-center gap-2"><Pill className="w-5 h-5" />AI-Suggested Medications</h3>
        <div className="space-y-4">
          {currentSuggestion.suggested_medications.map((med, index) => (
            <div key={`med-${index}`} className="border rounded-lg p-4 bg-gradient-to-r from-blue-50 to-purple-50">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="text-lg font-bold text-gray-800">{med.medication_name}</h4>
                  {med.generic_name && <p className="text-sm text-gray-600">Generic: {med.generic_name}</p>}
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${med.cost_category === 'low' ? 'bg-green-100 text-green-700' : med.cost_category === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>{med.cost_category} cost</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                <div><p className="text-xs text-gray-600">Dosage</p><p className="font-semibold">{med.dosage}</p></div>
                <div><p className="text-xs text-gray-600">Frequency</p><p className="font-semibold">{med.frequency}</p></div>
                <div><p className="text-xs text-gray-600">Duration</p><p className="font-semibold">{med.duration}</p></div>
                <div><p className="text-xs text-gray-600">Route</p><p className="font-semibold capitalize">{med.route}</p></div>
              </div>
              <div className="bg-white p-3 rounded mb-3"><p className="text-sm font-semibold text-gray-700 mb-1">Instructions:</p><p className="text-sm text-gray-600">{med.instructions}</p></div>
              <div className="bg-blue-50 p-3 rounded mb-3"><p className="text-sm font-semibold text-blue-700 mb-1">Why this medication:</p><p className="text-sm text-gray-700">{med.reason}</p></div>
              {med.alternatives.length > 0 && (
                <div><p className="text-sm font-semibold text-gray-700 mb-1">Alternatives:</p>
                  <div className="flex flex-wrap gap-2">{med.alternatives.map((alt, i) => <span key={`alt-${i}`} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">{alt}</span>)}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Lifestyle Recommendations */}
      {currentSuggestion.lifestyle_recommendations.length > 0 && (
        <Card className="p-6 bg-green-50 border-green-200">
          <h3 className="text-xl font-bold text-green-800 mb-3">Lifestyle Recommendations</h3>
          <ul className="list-disc ml-5 text-gray-700 space-y-1">
            {currentSuggestion.lifestyle_recommendations.map((rec, i) => <li key={`rec-${i}`}>{rec}</li>)}
          </ul>
        </Card>
      )}

      {/* Follow-up */}
      <Card className="p-6 bg-purple-50 border-purple-200">
        <h3 className="text-xl font-bold text-purple-800 mb-3 flex items-center gap-2"><Clock className="w-5 h-5" />Follow-up Advice</h3>
        <p className="text-gray-700">{currentSuggestion.follow_up_advice}</p>
      </Card>

      {/* Actions */}
      <div className="flex justify-center gap-4">
        <Button onClick={handleApprovePrescription} disabled={loading} size="lg" className="px-12 bg-green-600 hover:bg-green-700" data-testid="approve-prescription-btn">
          {loading ? 'Approving...' : 'Approve & Save Prescription'}
        </Button>
        <Button onClick={() => fetchMedicineLinks(currentSuggestion.id, 'ai')} variant="outline" size="lg" data-testid="order-medicines-btn" className="border-blue-500 text-blue-600 hover:bg-blue-50">
          <ShoppingCart className="w-5 h-5 mr-2" />Order Medicines
        </Button>
        <Button onClick={() => setActiveTab('create')} variant="outline" size="lg">Edit Patient Info</Button>
      </div>
    </div>
  );
};

export default ReviewSuggestions;
